import { useCallback, useEffect, useState } from "react";
import { api, isSignedOut, type Me } from "./lib/api";
import { KidHome, Shell } from "./routes/KidHome";
import { Centered, Login } from "./routes/Login";
import { ParentHome } from "./routes/ParentHome";
import { AddChild } from "./routes/AddChild";
import { ChildAdmin } from "./routes/ChildAdmin";
import { ChoreManager } from "./routes/ChoreManager";
import { ResetPassword } from "./routes/ResetPassword";
import { Setup } from "./routes/Setup";

type State =
  | { phase: "loading" }
  | { phase: "offline" }
  | { phase: "setup" }
  | { phase: "login" }
  | { phase: "reset-password"; token: string }
  | { phase: "kid"; me: Me }
  | { phase: "parent"; me: Me }
  | { phase: "child-admin"; me: Me; childId: string }
  | { phase: "add-child"; me: Me }
  | { phase: "chores"; me: Me };

/* Routing is a state machine rather than a URL router.
 *
 * This is a single-purpose household app on a shared tablet: there are no deep
 * links to share and no back-button journeys worth preserving. A state machine
 * makes the one rule that matters -- a child can only ever be in the child
 * screens -- checkable in one place instead of on every route.
 *
 * The one deliberate exception is /reset-password: a link opened from an
 * email client has to land somewhere, and it has to work regardless of
 * whatever session cookie this browser happens to be holding -- including one
 * for a different family member entirely. So it is read once, before
 * anything else, and short-circuits the sign-in resolution below rather than
 * living inside it.
 */
function resetTokenFromUrl(): string | null {
  if (window.location.pathname !== "/reset-password") return null;
  return new URLSearchParams(window.location.search).get("token");
}

export default function App() {
  const [state, setState] = useState<State>(() => {
    const token = resetTokenFromUrl();
    return token ? { phase: "reset-password", token } : { phase: "loading" };
  });

  const resolve = useCallback(async () => {
    if (resetTokenFromUrl()) return;

    // A 401 is the ONLY failure that means "you are signed out". Everything
    // else -- the API not running, a proxy 500, DNS, a half-started NAS --
    // means the server did not answer, and showing the sign-in screen for
    // those is what produced a "Who's here?" page with nobody on it and no
    // way forward. Name the actual problem instead.
    try {
      const me = await api.get<Me>("/auth/me");
      setState(me.role === "parent" ? { phase: "parent", me } : { phase: "kid", me });
      return;
    } catch (e) {
      if (!isSignedOut(e)) {
        setState({ phase: "offline" });
        return;
      }
    }

    // Signed out. Whether that means "set the family up" or "sign in" needs a
    // second call, which can fail the same way the first one did.
    try {
      const { needsSetup } = await api.get<{ needsSetup: boolean }>(
        "/auth/needs-setup",
      );
      setState({ phase: needsSetup ? "setup" : "login" });
    } catch {
      setState({ phase: "offline" });
    }
  }, []);

  useEffect(() => {
    void resolve();
  }, [resolve]);

  // Stable identity: Login depends on this in an effect, and a fresh arrow
  // every render would make that effect refetch the profile list.
  const goOffline = useCallback(() => setState({ phase: "offline" }), []);

  async function signOut() {
    await api.post("/auth/logout").catch(() => undefined);
    setState({ phase: "login" });
  }

  switch (state.phase) {
    case "loading":
      return <Centered>Loading&hellip;</Centered>;
    case "offline":
      return <Unreachable onRetry={resolve} />;
    case "setup":
      return <Setup onDone={resolve} />;
    case "login":
      return (
        <Login onSignedIn={resolve} onUnreachable={goOffline} />
      );
    case "reset-password":
      return (
        <ResetPassword
          token={state.token}
          onDone={() => {
            window.history.replaceState({}, "", "/");
            setState({ phase: "login" });
          }}
        />
      );
    case "kid":
      return <KidHome me={state.me} onSignOut={signOut} />;
    case "parent":
      return (
        <ParentHome
          me={state.me}
          onSignOut={signOut}
          onOpenChild={(childId) =>
            setState({ phase: "child-admin", me: state.me, childId })
          }
          onAddChild={() => setState({ phase: "add-child", me: state.me })}
          onManageChores={() => setState({ phase: "chores", me: state.me })}
        />
      );
    case "add-child":
      return (
        <Shell>
          <AddChild
            onDone={() => setState({ phase: "parent", me: state.me })}
            onCancel={() => setState({ phase: "parent", me: state.me })}
          />
        </Shell>
      );
    case "chores":
      return (
        <Shell>
          <ChoreManager onBack={() => setState({ phase: "parent", me: state.me })} />
        </Shell>
      );
    case "child-admin":
      return (
        <Shell>
          <button
            onClick={() => setState({ phase: "parent", me: state.me })}
            style={{ marginBottom: 16, minHeight: 44, fontSize: 14 }}
          >
            ← Back
          </button>
          <ChildAdmin
            childId={state.childId}
            onArchived={() => setState({ phase: "parent", me: state.me })}
          />
        </Shell>
      );
  }
}


/* The app loaded but its server did not answer.
 *
 * Worth its own screen rather than a toast: nothing else on the page can work
 * until this is fixed, and the two people who will ever see it -- a parent
 * whose NAS is still booting, and whoever is running it locally -- both need
 * to be told which half is missing.
 */
function Unreachable({ onRetry }: { onRetry: () => void }) {
  return (
    <Centered>
      <h1 style={{ fontSize: 28, marginBottom: 6 }}>Can&rsquo;t reach the server</h1>
      <p style={{ color: "var(--ink-2)", margin: "0 0 24px" }}>
        The app loaded, but the part that keeps the money didn&rsquo;t answer.
        It may still be starting up.
      </p>
      <button onClick={onRetry} style={{ minWidth: 180 }}>
        Try again
      </button>
    </Centered>
  );
}